import { ReactElement, Suspense, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import localStorage from 'utils/bom-dom-manipulation/local-storage';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { TThemeColors } from 'interfaces/data-spot';
import { AddToBetslipLinkCheck } from './__partials/AddToBetslipLinkCheck';
import { CheckDeRealityCheck } from './__partials/CheckDeRealityCheck';
import { CommunityChat } from './__partials/CommunityChat';
import { CurrencyRelatedLogic } from './__partials/CurrencyRelatedLogic';
import { FetchGenericSettings } from './__partials/FetchGenericSettings';
import { IconsPrefetcher } from './__partials/IconsPrefetcher';
import { InitializeFirebase } from './__partials/InitializeFirebase';
import { LoginRelatedLogic } from './__partials/LoginRelatedLogic';
import { PaggorUploadKYCVerificationCheck } from './__partials/PaggorLicenseKYCVerificationCheck';
import { PopupPortal } from './__partials/PopupPortal';
import { PostMessageRelatedLogic } from './__partials/PostMessageRelatedLogic';
import { RedirectFromStatistics } from './__partials/RedirectFromStatistics';
import { RedirectToSingleGameView } from './__partials/RedirectToSingleGameView';
import { SumSubKYCVerificationCheck } from './__partials/SumSubKYCVerificationCheck';
import { TourWrapper } from './__partials/TourWrapper';
import { TranslationsReloader } from './__partials/TranslationsReloader';
import { TurnstileCheck } from './__partials/TurnstileCheck';
import { BetBuilderModalGeneric } from 'components/Generic/__partials/BetBuilderModalGeneric';
import { DoBetRecaptcha } from 'components/Generic/__partials/DoBetRecaptcha';
import { GeolocationFailedPopup } from 'components/Generic/__partials/GeolocationFailedPopup';
import { KYCVerificationStatusBar } from 'components/Generic/__partials/KYCVerificationStatusBar';
import { LoyaltyDataFetcherWrapper } from 'components/Generic/__partials/LoyaltyDataFetcherWrapper';
import { SignicatVerificationBeforeActions } from 'components/Generic/__partials/SignicatVerificationBeforeActions';
import { WrongLoginAttemptsUnlockPopup } from 'components/Generic/__partials/WrongLoginAttemptsUnlockPopup';
import { QueryRoute } from 'components/QueryRoute';
import { NewDepositPopup } from 'newcomponents/Shared/account/NewDepositPopup';
import { AgePopup } from 'newcomponents/Shared/agePopup';
import { setIsDarkMode } from 'store/actions';
import { getIsDarkMode } from 'store/selectors/app-data';
import './index.less';

const { InitializeOneSignal } = lazy(
  () =>
    import(
      /* webpackChunkName: "initialize-one-signal" */ './__partials/InitializeOneSignal'
    )
);

const { InitializeFastTrack } = lazy(
  () =>
    import(
      /* webpackChunkName: "initialize-fast-track" */ './__partials/InitializeFastTrack'
    )
);

const { NotificationsRedirectionsHandler } = lazy(
  () =>
    import(
      /* webpackChunkName: "notifications-redirections-handler" */ './__partials/NotificationsRedirectionsHandler'
    )
);

const { HooryRelatedLogic } = lazy(
  () =>
    import(
      /* webpackChunkName: "hoory-features" */ './__partials/HooryRelatedLogic'
    )
);

const { BetModalCheck } = lazy(
  () =>
    import(
      /* webpackChunkName: "BetModal-Check" */ './__partials/BetModalCheck'
    )
);

const { Iovation } = lazy(
  () => import(/* webpackChunkName: "iovation" */ 'components/Iovation')
);

const { GamblingAreas } = lazy(
  () =>
    import(/* webpackChunkName: "gambling-areas" */ 'components/GambleingAreas')
);

const { CheckHomeworkData } = lazy(
  () =>
    import(
      /* webpackChunkName: "check-homework-data" */ 'newelements/Generics/homework/checkHomeworkData'
    )
);

const { LogoutRelatedLogic } = lazy(
  () =>
    import(
      /* webpackChunkName: "LogoutRelatedLogic" */ './__partials/LogoutRelatedLogic'
    )
);

const { IframeSportsbookLogic } = lazy(
  () =>
    import(
      /* webpackChunkName: "IframeSportsbookLogic" */ './__partials/IframeSportsbookLogic'
    )
);

const { CheckUserPersonalization } = lazy(
  () =>
    import(
      /* webpackChunkName: "Check-User-Personalization" */ './__partials/CheckUserPersonalization'
    )
);

const { CheckGeoComply } = lazy(
  () =>
    import(
      /* webpackChunkName: "Check-Geo-Comply" */ './__partials/CheckGeoComply'
    )
);

const { BrazilMigrateKYC } = lazy(
  () =>
    import(
      /* webpackChunkName: "BrazilMigrateKYC" */ './__partials/BrazilMigrateKYC'
    )
);

const { PagcorPopup } = lazy(
  () => import(/* webpackChunkName: "PagcorPopup" */ './__partials/PagcorPopup')
);

const { BottomNavigationOperator } = lazy(
  () =>
    import(
      /* webpackChunkName: "bottom-navigation-operator" */ 'elements/BottomNavigation/BottomNavigationOperator'
    )
);

const shouldMountBottomNavigationOperator =
  SpringConfigs.BOTTOM_NAVIGATION && isMobile() && window.fromNext;

const COLOR_SET = {
  buttonsColor: '',
  buttonsTextColor: '',
  themeColor: '',
  themeTextColor: '',
  pinnedWidgetColor: '',
  pinnedWidgetTextColor: ''
};

type TColorsKeys =
  | 'buttonsColor'
  | 'buttonsTextColor'
  | 'themeColor'
  | 'themeTextColor'
  | 'pinnedWidgetColor'
  | 'pinnedWidgetTextColor';

export const Generic = (): ReactElement | null => {
  const location = window.location.search;
  const dispatch = useDispatch();
  const isDarkMode = useSelector(getIsDarkMode);

  useEffect(() => {
    const theme = localStorage.getItem(storageKeyName('app', 'THEME'));

    dispatch(setIsDarkMode(theme === 'dark'));
  }, []);

  const themingChangeColors = useMemo(() => {
    const styleElem = getComputedStyle(document.body);
    const colorsArray: TThemeColors = COLOR_SET;

    const colorSet: TThemeColors = {
      buttonsColor: '--v3-primary-color',
      buttonsTextColor: '--v3-text-color',
      themeColor: '--v3-black-0',
      themeTextColor: '--v3-text-color',
      pinnedWidgetColor: '--v3-primary-color',
      pinnedWidgetTextColor: '--v3-color-on-primary'
    };

    Object.keys(colorSet).forEach(elem => {
      colorsArray[elem as TColorsKeys] = styleElem.getPropertyValue(
        colorSet[elem as TColorsKeys]
      );
    });

    return { ...colorsArray };
  }, [isDarkMode]);

  useEffect(() => {
    window.__dataspot__?.trackThemingChange?.({
      themeColors: themingChangeColors
    });
  }, [themingChangeColors]);

  return (
    <>
      {location.includes('message=') && <NewDepositPopup />}
      {SpringConfigs.PUSH_NOTIFICATIONS_ENABLED && (
        <Suspense>
          <QueryRoute
            includes={['notificationRoute']}
            component={NotificationsRedirectionsHandler}
          />
        </Suspense>
      )}
      {SpringConfigs.GAMBLING_AREAS && !SpringConfigs.MOCKED_DATA && (
        <Suspense>
          <GamblingAreas />
        </Suspense>
      )}
      {SpringConfigs.VERIFY_AGE_POPUP ? <AgePopup /> : null}
      {SpringConfigs.IFRAME_SPORTSBOOK && (
        <Suspense>
          <IframeSportsbookLogic />
        </Suspense>
      )}
      {SpringConfigs.IS_BET_SHARING_AVAILABLE && (
        <Suspense>
          <BetModalCheck />
        </Suspense>
      )}
      {(!!SpringConfigs.SESSION_LIFE_TIME ||
        SpringConfigs.INACTIVITY_LOGOUT_ENABLED) && (
        <Suspense>
          <LogoutRelatedLogic />
        </Suspense>
      )}
      {SpringConfigs.FIREBASE_ENABLED && (
        <Suspense>
          <InitializeFirebase />
        </Suspense>
      )}
      {SpringConfigs.HOMEWORK_ENABLED && (
        <Suspense>
          <CheckHomeworkData />
        </Suspense>
      )}
      {SpringConfigs.HOORY_ENABLED && (
        <Suspense>
          <HooryRelatedLogic />
        </Suspense>
      )}
      {SpringConfigs.IOVATION_ENABLED && (
        <Suspense>
          <Iovation />
        </Suspense>
      )}
      {SpringConfigs.ONE_SIGNAL_APP_ID && (
        <Suspense>
          <InitializeOneSignal />
        </Suspense>
      )}
      {SpringConfigs.USER_PERSONALIZATION_ENABLED &&
        !SpringConfigs.MOCKED_DATA && (
          <Suspense>
            <CheckUserPersonalization />
          </Suspense>
        )}
      {SpringConfigs.FAST_TRACK_BRAND && (
        <Suspense>
          <InitializeFastTrack />
        </Suspense>
      )}
      {SpringConfigs.GEO_COMPLY_ENABLED && (
        <Suspense>
          <CheckGeoComply />
        </Suspense>
      )}
      {SpringConfigs.SHOW_ADDITIONAL_CHECKBOX_FOR_LOGIN &&
        SpringConfigs.VERIFICATION_TYPE === 1 && (
          <Suspense>
            <BrazilMigrateKYC />
          </Suspense>
        )}

      {SpringConfigs.FACIAL_VERIFICATION_BEFORE_ACTIONS && (
        <Suspense>
          <SignicatVerificationBeforeActions />
        </Suspense>
      )}

      {SpringConfigs.PAGCOR_POPUP && (
        <Suspense>
          <PagcorPopup />
        </Suspense>
      )}

      {shouldMountBottomNavigationOperator && (
        <Suspense>
          <BottomNavigationOperator />
        </Suspense>
      )}

      {window.fromNext && <TranslationsReloader />}

      <PaggorUploadKYCVerificationCheck />
      <WrongLoginAttemptsUnlockPopup />
      <SumSubKYCVerificationCheck />
      <LoyaltyDataFetcherWrapper />
      <RedirectToSingleGameView />
      <KYCVerificationStatusBar />
      <PostMessageRelatedLogic />
      <GeolocationFailedPopup />
      <RedirectFromStatistics />
      <BetBuilderModalGeneric />
      <AddToBetslipLinkCheck />
      <CurrencyRelatedLogic />
      <FetchGenericSettings />
      <CheckDeRealityCheck />
      <LoginRelatedLogic />
      <DoBetRecaptcha />
      <IconsPrefetcher />
      <CommunityChat />
      <PopupPortal />
      <TurnstileCheck />
      <TourWrapper />
    </>
  );
};
